import { Chip } from '@heroui/react';
import { LessonTypeEnum } from '@/shared/types/lesson';
import { formatDate } from '@/shared/lib/utils/formatDate';

export interface ITeacherAssignmentInfo {
  course: {
    title: string;
  };
  intake: {
    title: string;
  };
  group: {
    number: number;
  };
  totalStudents: number;
  totalCompleted: number;
  module: {
    title: string;
  };
  lesson: {
    order: number;
    title: string;
    type: LessonTypeEnum;
    scheduledAt: string;
    endDate: string | null;
    assignmentInfo: {
      awaitingReview?: number;
      grade?: number;
      matchesPercentage?: number;
    };
  };
}

interface TeacherAssignmentInfoProps {
  data: ITeacherAssignmentInfo;
}

export const TeacherAssignmentInfo = ({ data }: TeacherAssignmentInfoProps) => {
  const { course, intake, group, totalStudents, module, lesson } = data;

  return (
    <>
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-[20px] font-medium text-black-pure">
            {lesson.order}. {lesson.title}
          </h2>
          <h3 className="text-[20px] font-medium text-black-pure">Module: {module.title}</h3>
          <div className="flex items-center gap-2 text-sm text-grey-secondary mt-1 flex-wrap">
            <span className="text-black font-medium">
              {course.title} {intake.title}
            </span>
            <span className="text-neutral-border">|</span>
            <span>
              Group: <span className="text-black-pure">{group.number}</span>
            </span>
            <span className="text-neutral-border">|</span>
            <span>
              Students: <span className="text-black-pure">{totalStudents}</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {lesson.type !== LessonTypeEnum.Regular && (
            <Chip className="bg-[#FFF0EE] py-1 px-5 rounded-full text-red-tertiary font-bold">
              {lesson.type.charAt(0).toUpperCase() + lesson.type.slice(1)}
            </Chip>
          )}
          {typeof lesson.assignmentInfo.awaitingReview === 'number' && (
            <p className="text-sm text-grey-secondary">
              To review:{' '}
              <span className="text-black-pure font-medium">
                {lesson.assignmentInfo.awaitingReview}
              </span>
            </p>
          )}
          {typeof lesson.assignmentInfo.grade === 'number' && (
            <p className="text-sm text-grey-secondary">
              Grade:{' '}
              <span className="text-black-pure font-medium">{lesson.assignmentInfo.grade}</span>
            </p>
          )}
          {typeof lesson.assignmentInfo.matchesPercentage === 'number' && (
            <p className="text-sm text-grey-secondary">
              Similarity:{' '}
              <span className="text-black-pure font-medium">
                {lesson.assignmentInfo.matchesPercentage}%
              </span>
            </p>
          )}
        </div>
      </div>
      <ul className="inline-flex gap-x-[30px] text-sm">
        <li className="text-grey-secondary font-medium [&:not(:first-child)]:list-disc [&:not(:first-child)]:marker:text-black">
          Created: <span className="text-black">{formatDate(lesson.scheduledAt)}</span>
        </li>
        {lesson.endDate && (
          <li className="text-grey-secondary font-medium [&:not(:first-child)]:list-disc [&:not(:first-child)]:marker:text-black">
            Deadline: <span className="text-black">{formatDate(lesson.endDate)}</span>
          </li>
        )}
      </ul>
    </>
  );
};
